import * as React from 'react';
import {TText, EText, type Expression} from '@packages/systems/wfdl-ast';
import {
  ExpressionEditor,
  updateExpressionAction,
} from '@packages/systems/ExpressionEditor-utils';
import Hitarea from '@packages/systems/design-system/Hitarea';
import TextInput from '@packages/systems/design-system/TextInput';
import {toStringId, isReservedId} from '@packages/systems/core/utils/HtmlUtils';
import {getBody} from '@packages/utilities/platform';
import {getLiveComponentMap} from '@packages/utilities/platform-selectors';
import {Note} from '@packages/systems/spring';
import {Text} from '@packages/systems/design-system/Text';
import {
  TypeImplementation,
  Expressions,
  matchExpression,
  Stoppable,
  Component,
  ExpressionPath,
} from '@packages/systems/wfdl';
import {
  type ContextualExpression,
  stripAll,
} from '@packages/utilities/contextual-expression';
import {DesignerFlux, useFluxContext} from '@packages/systems/designer/flux';

export const ID_ATTR_PATH = [
  {in: 'Record', at: 'attr'},
  {in: 'Record', at: 'id'},
] satisfies ExpressionPath;

type IdStatus = 'taken' | 'reserved' | 'invalid' | 'valid';

const styledNote = {
  width: '100%',
  marginTop: 8,
} as const;

const warningText = {
  taken: 'This ID is already taken.',
  reserved: 'This ID is reserved for Webflow features.',
  invalid: 'This ID is not valid.',
} as const;

const getIds: (arg1: Expression) => Stoppable<false | Set<string>> =
  matchExpression({
    Element: ({data}) => {
      const fields = Expressions.getRecord(data);
      const attrExpr = fields ? fields.attr : null;
      if (!attrExpr) {
        return Expressions.resume(false);
      }
      const attrFields = Expressions.getRecord(attrExpr);
      const idExpr = attrFields ? attrFields.id : null;
      if (!idExpr) {
        return Expressions.resume(false);
      }
      const customId = Expressions.getText(idExpr);
      if (!customId) {
        return Expressions.resume(false);
      }
      // Gather up all the IDs
      return Expressions.resume(new Set([customId]));
    },
    _: () => Expressions.resume(false),
  });

const appendSet =
  (x: Set<string> | false) =>
  (y: Set<string> | false): Set<string> | false => {
    if (x !== false && y !== false) return new Set([...x, ...y]);
    if (x === false && y !== false) return y;
    if (x !== false && y === false) return x;

    return false;
  };

const createTakenIdAttributesSet = Expressions.everything(appendSet)(getIds);

function useTakenIdAttributes(
  componentMap: Component.ComponentMap
): Set<string> {
  return React.useMemo(() => {
    const body = getBody(componentMap);
    if (!body) throw Error('body expression not found');
    const bodyExpression = Expressions.getElementData(body);
    return createTakenIdAttributesSet(bodyExpression) || new Set();
    // We are intentionally not including any dependencies
    // here to make sure this fn is only ran once
    // eslint-disable-next-line react-hooks/react-compiler -- pre-ratchet baseline (BLOOP-3033)
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);
}

const IdTextInputRender = ({
  expr,
  dispatch,
}: {
  expr: ContextualExpression;
  dispatch: TypeImplementation.ExpressionEditorDispatch;
}) => {
  const strippedExpr = stripAll(expr);
  const currExprId = Expressions.getText(strippedExpr);
  const [currId, setCurrId] = React.useState(currExprId);
  const [idStatus, setIdStatus] = React.useState<IdStatus>('valid');

  // Make sure to update the current ID when the expression changes
  React.useEffect(() => {
    setCurrId(currExprId);
  }, [currExprId]);

  const flux = useFluxContext<DesignerFlux>();
  const componentMap = getLiveComponentMap(flux.state.DesignerStore);

  const takenIdAttributes = useTakenIdAttributes(componentMap);

  if (currId !== null && currExprId !== null) {
    const onIdCommit = () => {
      const idAttr = toStringId(currId);
      if (idAttr && idStatus !== 'reserved') {
        dispatch(updateExpressionAction(EText(idAttr)));
        setCurrId(idAttr);
      } else {
        dispatch(updateExpressionAction(EText('')));
        setCurrId('');
      }
      setIdStatus('valid');
    };

    const onChange = (e: React.ChangeEvent<HTMLInputElement>) => {
      const val = e.target.value;
      const idAttr = toStringId(val);

      setCurrId(val);
      if (idAttr && takenIdAttributes.has(idAttr) && idAttr !== currExprId) {
        setIdStatus('taken');
      } else if (idAttr && isReservedId(idAttr)) {
        setIdStatus('reserved');
      } else if (val !== '' && idAttr === null) {
        setIdStatus('invalid');
      } else {
        setIdStatus('valid');
      }
    };

    const handleEnter = (e: React.KeyboardEvent<HTMLInputElement>) => {
      if (e.key === 'Enter') onIdCommit();
    };

    return (
      <div>
        <Hitarea>
          <TextInput
            data-automation-id="id-attribute-input"
            type="text"
            aria-label="ID attribute input"
            placeholder="For in-page linking"
            value={currId}
            onKeyDown={handleEnter}
            onBlur={onIdCommit}
            onChange={onChange}
          />
        </Hitarea>
        {idStatus !== 'valid' ? (
          <Note style={styledNote} colorVariant="warning" hasIcon={false}>
            <Text>{warningText[idStatus]}</Text>
          </Note>
        ) : null}
      </div>
    );
  }

  return <ExpressionEditor dispatch={dispatch} expression={expr} />;
};

export const IdTextInputType = TypeImplementation.Alias({
  type: TText,
  render: IdTextInputRender,
  system: {
    displayName: 'ID',
  },
});
